import React, { useMemo } from 'react';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';
import { useListAllBrokerGroupsQuery } from '../../brokerGroups/api/brokerGroupsApi';
import { BROKER_GROUP_SEARCH_OPTIONS } from '../searchOptions';
import { ADMIN_PICKER_OPTION_LIMIT } from './constants';
import { fuseFilterOptions } from './fuseFilterOptions';

const byLedger = (a, b) => (a.ledger ?? '').localeCompare(b.ledger ?? '');

// Matches ranked by Fuse, then regrouped by ledger (keeping the ranking within each ledger)
// so the ledger headings don't repeat.
const filterBrokerGroups = (options, state) =>
	[...fuseFilterOptions(BROKER_GROUP_SEARCH_OPTIONS)(options, state)].sort(byLedger);

/**
 * Pick broker groups from every broker group, searchable by name and ledger, grouped by
 * ledger, listing at most ADMIN_PICKER_OPTION_LIMIT matches at a time.
 * value/onChange are broker group ids (an array when multiple, the default). excludeIds are
 * left out of the options; when includeIds is given, only those are offered. A chosen id that
 * isn't a broker group (e.g. from an import file)
 * still shows, as "<id> (not found)" under "Not found", so it can be removed. Other props go
 * to ResponsiveAutocomplete.
 */
export const AdminBrokerGroupPicker = ({
	excludeIds = [],
	includeIds,
	multiple = true,
	value,
	...props
}) => {
	const { data: brokerGroups, isLoading } = useListAllBrokerGroupsQuery();

	const options = useMemo(() => {
		const chosenIds = (multiple ? (value ?? []) : [value]).filter(Boolean);
		const notFound = brokerGroups
			? chosenIds
				.filter((id) => !brokerGroups.some((brokerGroup) => brokerGroup.id === id))
				.map((id) => ({ id, name: `${id} (not found)`, ledger: 'Not found' }))
			: [];
		return [
			...notFound,
			...(brokerGroups ?? [])
				.filter(
					(brokerGroup) =>
						!excludeIds.includes(brokerGroup.id) &&
						(!includeIds || includeIds.includes(brokerGroup.id)),
				)
				.sort((a, b) => byLedger(a, b) || (a.name ?? '').localeCompare(b.name ?? '')),
		];
	}, [brokerGroups, excludeIds, includeIds, multiple, value]);

	return (
		<ResponsiveAutocomplete
			options={options}
			value={value}
			groupBy='ledger'
			multiple={multiple}
			searchable
			filterOptions={filterBrokerGroups}
			optionLimit={ADMIN_PICKER_OPTION_LIMIT}
			loading={isLoading}
			disabled={isLoading}
			fitMenuToContent
			{...props}
		/>
	);
};
